/**
 * Quality NCR list column configuration.
 *
 * Mirrors the Schedule Lines column-manager pattern (types.ts +
 * load/save + drag reorder). Column set sourced from the legacy
 * SharePoint Corrective/Preventative Action List that customers
 * still expect parity with.
 *
 * The 5 columns marked `dataMissing: true` are intentionally listed
 * with a "—" placeholder render — the underlying NCR model doesn't
 * carry the value yet, but the column is exposed so the customer
 * can see what's expected and request the model addition.
 */
import type { ReactNode } from 'react';
import type { NonConformanceReport } from '../../lib/api/types/quality';

export interface NCRColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  /** API ordering field name when sortable. Falls back to id. */
  sortField?: string;
  minWidth: string;
  order: number;
  /** True when the spec column has no backing model data — renders as "—". */
  dataMissing?: boolean;
}

export const NCR_COLUMN_STORAGE_KEY = 'quality_ncr_column_config';

/**
 * Default column set — order, visibility, and labels match the
 * Dennis Eagle SharePoint export (`Columns.xlsx`). Visibility off-by-default
 * for less-used columns to keep first-load uncluttered.
 */
export const DEFAULT_NCR_COLUMNS: NCRColumnConfig[] = [
  { id: 'ncr_number', label: 'NCR No.', visible: true, sortable: true, sortField: 'ncr_number', minWidth: '110px', order: 0 },
  { id: 'raised_date', label: 'Created', visible: true, sortable: true, sortField: 'raised_date', minWidth: '140px', order: 1 },
  { id: 'raised_by_name', label: 'Created By', visible: true, sortable: false, minWidth: '160px', order: 2 },
  // CR-6027: surface the NCR's customer company directly in the list so
  // reviewers don't have to open each record to see who raised it.
  // Backed by `company_name` (source='company.name') on
  // NonConformanceReportListSerializer; ordering goes through
  // company__name which is already in the ViewSet's ordering_fields.
  { id: 'company_name', label: 'Customer', visible: true, sortable: true, sortField: 'company_name', minWidth: '180px', order: 3 },
  { id: 'supplier_code', label: 'Supplier Code', visible: false, sortable: false, minWidth: '120px', order: 4 },
  { id: 'supplier_name', label: 'Supplier Name', visible: true, sortable: true, sortField: 'supplier_name', minWidth: '180px', order: 5 },
  // Supplier quality master data, uploaded against supplier code. These read
  // from the supplier company, so they populate on every NCR for that supplier
  // — including ERP-imported ones — with no per-NCR data entry.
  { id: 'supplier_uk_eu', label: 'UK/EU', visible: false, sortable: false, minWidth: '90px', order: 6 },
  { id: 'part_number', label: 'Part No.', visible: true, sortable: true, sortField: 'part_number', minWidth: '120px', order: 7 },
  { id: 'defect_description', label: 'Defect Description', visible: true, sortable: false, minWidth: '240px', order: 8 },
  { id: 'liability_display', label: 'Liability', visible: true, sortable: false, minWidth: '130px', order: 9 },
  { id: 'is_internal', label: 'Internal', visible: false, sortable: false, minWidth: '90px', order: 9.1 },
  { id: 'assigned_to_name', label: 'Assigned To', visible: false, sortable: false, minWidth: '160px', order: 9.2 },
  { id: 'proposed_rejection_display', label: 'Action to Resolve', visible: true, sortable: false, minWidth: '160px', order: 10 },
  { id: 'collection_date', label: 'Collection Date', visible: false, sortable: false, minWidth: '130px', order: 11 },
  { id: 'status_display', label: 'Action Status', visible: true, sortable: true, sortField: 'status', minWidth: '120px', order: 12 },
  { id: 'immediate_corrective_action', label: 'Immediate Containment Action', visible: false, sortable: false, minWidth: '260px', order: 13 },
  { id: 'selected_root_cause', label: 'Root Cause', visible: false, sortable: false, minWidth: '240px', order: 13.1 },
  { id: 'corrective_action', label: 'Corrective Action', visible: false, sortable: false, minWidth: '240px', order: 13.2 },
  { id: 'preventive_action', label: 'Preventive Action', visible: false, sortable: false, minWidth: '240px', order: 13.3 },
  { id: 'supplier_contact_name', label: 'Supplier Contact', visible: false, sortable: false, minWidth: '180px', order: 14 },
  { id: 'supplier_contact_email', label: 'Supplier Contact Email', visible: false, sortable: false, minWidth: '200px', order: 14.1 },
  // The customer's engineer who owns this supplier — CR-6794 resolves it per
  // customer, so two customers sharing a supplier no longer see one another's.
  // quality_engineer_feedback is a different thing (8D prose on the NCR) and
  // keeps its own column below.
  { id: 'supplier_quality_engineer', label: 'Quality Engineer', visible: false, sortable: false, minWidth: '180px', order: 15 },
  { id: 'quality_engineer_feedback', label: 'Quality Engineer Feedback', visible: false, sortable: false, minWidth: '200px', order: 15.1 },
  { id: 'reject_qty', label: 'Reject Qty', visible: true, sortable: false, minWidth: '90px', order: 16 },
  { id: 'unit_price', label: 'Unit Cost', visible: true, sortable: false, minWidth: '100px', order: 17 },
  { id: 'reject_value', label: 'Total Price', visible: true, sortable: false, minWidth: '110px', order: 18 },
  { id: 'commodity', label: 'Commodity', visible: false, sortable: false, minWidth: '160px', order: 19, dataMissing: true },
  { id: 'cost_of_rework', label: 'Cost of Rework', visible: false, sortable: false, minWidth: '120px', order: 20, dataMissing: true },
  { id: 'is_8d_required', label: '8D/A3 Required?', visible: false, sortable: false, minWidth: '100px', order: 21 },
  { id: 'date_8d_raised', label: 'Date 8D/A3 raised', visible: false, sortable: false, minWidth: '150px', order: 22 },
  { id: 'ca_submitted_date', label: 'Containment Date', visible: false, sortable: false, minWidth: '150px', order: 23 },
  // Sourced from car_submitted_date (when the CAR/8D was submitted) — DE calls
  // this the 8D/A3 submission date (customer review 2026-07-15).
  { id: 'car_submitted_date', label: '8D/A3 Submission Date', visible: false, sortable: false, minWidth: '180px', order: 24 },
  { id: 'tech_days', label: 'Tech Days', visible: false, sortable: false, minWidth: '100px', order: 24.1 },
  { id: 'engineer_days', label: "Engineer's Days", visible: false, sortable: false, minWidth: '110px', order: 24.2 },
  // 8D scoring: customer CAR grades + calculated columns.
  // RC / CA / 8D-A3 scores are shown by default (customer review 2026-07-15):
  // the scoring feature is always-on, so the register surfaces the grades
  // without the customer having to enable the columns manually.
  { id: 'rc_score', label: 'RC Score', visible: true, sortable: false, minWidth: '90px', order: 24.3 },
  { id: 'ca_score', label: 'CA Score', visible: true, sortable: false, minWidth: '90px', order: 24.4 },
  { id: 'close_off_days_8d', label: '8D/A3 Close off days', visible: false, sortable: false, minWidth: '150px', order: 24.5 },
  { id: 'action_completion_date', label: '8D/A3 Action Completion Date', visible: false, sortable: false, minWidth: '190px', order: 24.65 },
  { id: 'score_8d', label: '8D/A3 Score', visible: true, sortable: false, minWidth: '100px', order: 24.6 },
  // Customer review 2026-07: commonised CA + repeat/containment flags
  { id: 'commonised_corrective_action_name', label: 'Commonised Corrective Action', visible: false, sortable: false, minWidth: '220px', order: 24.7 },
  { id: 'is_repeat_issue', label: 'Repeat Issue', visible: false, sortable: false, minWidth: '110px', order: 24.8 },
  { id: 'containment_required', label: 'Containment Requested', visible: false, sortable: false, minWidth: '170px', order: 24.9 },
  { id: 'credit_debit_no', label: 'Credit / Debit No.', visible: false, sortable: false, minWidth: '140px', order: 25 },
  { id: 'debit_note_raised', label: 'Debit Note Raised', visible: false, sortable: false, minWidth: '140px', order: 26 },
  { id: 'defect_photo_count', label: 'Defect Photo', visible: false, sortable: false, minWidth: '110px', order: 27 },
  { id: 'is_credit_required', label: 'Claim Status', visible: false, sortable: false, minWidth: '120px', order: 28 },
  { id: 'closed_date', label: 'Date Closed', visible: false, sortable: true, sortField: 'closed_date', minWidth: '130px', order: 29 },
  { id: 'last_modified_by_name', label: 'Modified By', visible: false, sortable: false, minWidth: '160px', order: 30 },
  { id: 'latest_supplier_comment', label: 'Supplier Comments', visible: false, sortable: false, minWidth: '260px', order: 31 },
  { id: 'quantity_scrapped', label: 'Scrapped Qty', visible: false, sortable: false, minWidth: '110px', order: 32 },
];

export function loadNCRColumns(): NCRColumnConfig[] {
  try {
    const stored = localStorage.getItem(NCR_COLUMN_STORAGE_KEY);
    if (!stored) return DEFAULT_NCR_COLUMNS;
    const parsed = JSON.parse(stored) as NCRColumnConfig[];
    const storedMap = new Map(parsed.map((c) => [c.id, c]));
    return DEFAULT_NCR_COLUMNS.map((def) => {
      const saved = storedMap.get(def.id);
      return saved ? { ...def, visible: saved.visible, order: saved.order } : def;
    }).sort((a, b) => a.order - b.order);
  } catch {
    return DEFAULT_NCR_COLUMNS;
  }
}

export function saveNCRColumns(cols: NCRColumnConfig[]) {
  localStorage.setItem(NCR_COLUMN_STORAGE_KEY, JSON.stringify(cols));
}

/**
 * Render the cell value for a given column. Returns ReactNode so callers can
 * pass formatters (date, number) without per-row branching at the call site.
 *
 * `dataMissing` columns short-circuit to the dash so the cell remains
 * visually present but obviously empty — pairs with the column-manager
 * dropdown footnote so the customer knows it's expected.
 */
export function renderNCRCell(
  col: NCRColumnConfig,
  ncr: NonConformanceReport,
  helpers: {
    formatDate: (d: string | Date) => string;
    formatNumber: (n: number) => string;
  },
): ReactNode {
  if (col.dataMissing) return <span className="text-muted-foreground/50">&mdash;</span>;
  const dash = <span className="text-muted-foreground/50">&mdash;</span>;
  const val = (ncr as unknown as Record<string, unknown>)[col.id];
  switch (col.id) {
    case 'ncr_number':
      // Title in the spec is always the user-facing NCR number; fall back to
      // a dash rather than the DB primary key so the table never shows
      // a 5-digit internal id where users expect a "70350"-style identifier.
      return ncr.ncr_number || dash;
    case 'raised_date':
      return ncr.raised_date ? helpers.formatDate(ncr.raised_date) : dash;
    case 'closed_date':
      return ncr.closed_date ? helpers.formatDate(ncr.closed_date) : dash;
    case 'collection_date':
      return ncr.collection_date ? helpers.formatDate(ncr.collection_date) : dash;
    case 'date_8d_raised':
      return ncr.date_8d_raised ? helpers.formatDate(ncr.date_8d_raised) : dash;
    case 'ca_submitted_date':
      return ncr.ca_submitted_date ? helpers.formatDate(ncr.ca_submitted_date) : dash;
    case 'car_submitted_date':
      return ncr.car_submitted_date ? helpers.formatDate(ncr.car_submitted_date) : dash;
    case 'action_completion_date':
      return ncr.action_completion_date ? helpers.formatDate(ncr.action_completion_date) : dash;
    case 'reject_qty':
    case 'quantity_scrapped':
    case 'quantity_reworked':
      return typeof val === 'number' ? helpers.formatNumber(val) : dash;
    // 8D scoring: plain integers (scores 1-5, working-day count, RC*CA)
    case 'rc_score':
    case 'ca_score':
    case 'close_off_days_8d':
    case 'score_8d':
      return typeof val === 'number' ? String(val) : dash;
    case 'unit_price':
      return ncr.unit_price ? helpers.formatNumber(parseFloat(ncr.unit_price)) : dash;
    case 'reject_value':
      return typeof ncr.reject_value === 'number' ? helpers.formatNumber(ncr.reject_value) : dash;
    case 'is_8d_required':
      return ncr.is_8d_required ? 'Yes' : 'No';
    case 'debit_note_raised':
      return ncr.debit_note_raised ? 'Yes' : 'No';
    case 'is_repeat_issue':
      return ncr.is_repeat_issue
        ? <span className="text-red-700 font-medium">Yes</span>
        : 'No';
    case 'containment_required':
      return ncr.containment_required
        ? (ncr.containment_confirmed ? 'Contained' : 'Requested')
        : dash;
    case 'is_internal':
      return ncr.is_internal ? 'Internal' : dash;
    case 'is_credit_required': {
      // "Claim Status": a claim is resolved by EITHER a credit or a
      // replacement, so the column is Required when either flag is set.
      // Customer review 2026-07-08 (DMR 71215): the column now PROGRESSES —
      // once every required claim workflow reaches its terminal state it
      // shows Processed instead of sitting on Required forever. The
      // 'Processed' stamp comes from the customer approving the credit
      // note / replacement receipt in Helix (there is no automatic
      // disposition feed from the ERP).
      const creditNeeded = !!ncr.is_credit_required;
      const replacementNeeded = !!ncr.is_replacement_required;
      if (!creditNeeded && !replacementNeeded) return 'Not Required';
      const creditDone = !creditNeeded || !!ncr.credit_completed;
      const replacementDone = !replacementNeeded || !!ncr.replacement_completed;
      return (creditDone && replacementDone)
        ? <span className="text-emerald-700 font-medium">Processed</span>
        : 'Required';
    }
    case 'defect_description':
    case 'immediate_corrective_action':
    case 'selected_root_cause':
    case 'corrective_action':
    case 'preventive_action':
    case 'latest_supplier_comment':
    case 'quality_engineer_feedback':
    case 'supplier_uk_eu':
    case 'supplier_contact_name':
    case 'supplier_quality_engineer':
    case 'supplier_contact_email': {
      const text = String(val ?? '').trim();
      if (!text) return dash;
      // Truncate long text to keep rows scannable; full value stays in tooltip.
      return <span title={text}>{text.length > 80 ? `${text.slice(0, 78)}…` : text}</span>;
    }
    case 'defect_photo_count': {
      const n = ncr.defect_photo_count ?? 0;
      return n > 0 ? `${n} photo${n === 1 ? '' : 's'}` : dash;
    }
    default:
      if (val === null || val === undefined || val === '') return dash;
      return String(val);
  }
}
